import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    addEdge,
    useNodesState,
    useEdgesState,
    applyNodeChanges
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import DefaultNode from "./Nodes/DefaultNode";
import AgentAiChat from "../AIChat/AgentAiChat";
import { useNavigate, useParams } from "react-router-dom";
import { getLastWordsContext } from "../Functions/GetLastWords"
import TriggerNode from "./Nodes/Trigger";
import WorkflowLoader from "../Loaders/WorkflowLoader";
import { TbLayoutSidebarLeftExpand, TbLayoutSidebarRightExpandFilled } from "react-icons/tb";
import FlowFrame from "./FlowFrame";
import ConditionalNodes from "./Nodes/ConditionalNodes";
import AgentNode from "./Nodes/AgentNode";
import NotesNode from "./Nodes/NotesNode";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const GEN_AI_LINK = process.env.REACT_APP_GEN_AI_LINK
const GEN_WORKFLOW_LINK = ""

const initialNodes = [
    {
        id: "1",
        position: { x: 450, y: 250 },
        type: "default",
        data: {
            label: "Start",
            onClick: null,
        },
    },
];

const NODE_REGISTRY = {
    note: {
        type: "note",
        build: (item) => ({
            value: item.value,
            label: item.label,
            content: item.content || "",
        }),
    },

    schedule: {
        type: "schedule",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    manual: {
        type: "manual",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    // FLOW NODES
    if: {
        type: "if",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    if_else: {
        type: "if_else",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    switch: {
        type: "switch",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    filter: {
        type: "filter",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    loop: {
        type: "loop",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    while: {
        type: "while",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    break: {
        type: "break",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    continue: {
        type: "continue",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },

    agent: {
        type: "agent",
        build: (item) => ({
            value: item.value,
            label: item.label,
            description: item.description,
        }),
    },
};

const initialEdges = [];

const CreateWorkflow = ({
    account_id,
    mcpData,
    fetchMcpData,
    loadingMcpData
}) => {
    const navigate = useNavigate()
    const { org_id, wf_id } = useParams()
    const dropdownRef = useRef(null);
    const [openOverDropdown, setOpenOverDropdown] = useState(false)
    const [nodes, setNodes, _onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const [loadingWfData, setLoadingWfData] = useState(false)
    const [wfData, setWfData] = useState([])
    const [openAiChat, setOpenAichat] = useState(true)
    const [typeNodeTrigger, setTypeNodeTrigger] = useState(false)
    const [orderedNodes, setOrderedNodes] = useState(null)
    const [executionOutput, setExecutionOutput] = useState(null);
    const [executionHistory, setExecutionHistory] = useState([]);
    const [pendingConnectionSource, setPendingConnectionSource] = useState(null);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [openInputWorkflow, setOpenInputWorkflow] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const FIRST_POSITION = { x: 450, y: 250 };
    const SPACING_X = 220;
    const MIN_DISTANCE = 140;

    const toggleCodeView = () => {
        openAiChat ? setOpenAichat(false) : setOpenAichat(true)
    }

    const toggleOverDropdown = () => {
        openOverDropdown ? setOpenOverDropdown(false) : setOpenOverDropdown(true)
    }

    const withAddNext = useCallback((Component) => {
        return (props) => (
            <Component
                {...props}
                data={{
                    ...props.data,
                    id: props.id,
                    onAddNext: (nodeId) => {
                        setPendingConnectionSource(nodeId);
                        setOpenOverDropdown(true);
                    }
                }}
            />
        );
    }, [setPendingConnectionSource, setOpenOverDropdown]);


    const nodeTypes = useMemo(() => ({
        note: NotesNode,
        manual: withAddNext(TriggerNode),
        schedule: withAddNext(TriggerNode),
        if: withAddNext(ConditionalNodes),
        if_else: withAddNext(ConditionalNodes),
        switch: withAddNext(ConditionalNodes),
        filter: withAddNext(ConditionalNodes),
        loop: withAddNext(ConditionalNodes),
        while: withAddNext(ConditionalNodes),
        break: withAddNext(ConditionalNodes),
        continue: withAddNext(ConditionalNodes),
        agent: withAddNext(AgentNode),
        default: withAddNext(DefaultNode),
    }), [setPendingConnectionSource, setOpenOverDropdown]);

    const buildGraph = (nodes, edges) => {
        const graph = new Map();

        nodes.forEach(n => graph.set(n.id, []));

        edges.forEach(e => {
            if (!graph.has(e.source)) graph.set(e.source, []);
            graph.get(e.source).push(e.target);
        });

        return graph;
    };

    const enrichNodesWithExecution = (nodes = []) => {
        if (!Array.isArray(nodes)) return [];

        return nodes.map(node => ({
            ...node,
            className: node.id === selectedNodeId ? "selected" : "",
            data: {
                ...node.data,
                lastOutput: executionHistory[node.id] || null
            }
        }));
    };

    const buildExecutionGraph = (nodes, edges) => {
        const graph = buildGraph(nodes, edges);
        const visited = new Set();

        const executionMap = {};

        let lastExecution = executionOutput;

        const dfs = (nodeId) => {
            if (!executionMap[nodeId]) {
                executionMap[nodeId] = {
                    execute: true,
                    next: []
                };
            }

            const children = graph.get(nodeId) || [];

            children.forEach(childId => {
                executionMap[nodeId].next.push(childId);

                if (!visited.has(childId)) {
                    visited.add(childId);
                    dfs(childId);
                }
            });
        };

        const startNodes = nodes.filter(n => n.type === "manual" || n.type === "schedule");

        startNodes.forEach(n => {
            visited.add(n.id);
            dfs(n.id);
        });

        return nodes.map(node => ({
            ...node,
            data: {
                ...node.data,
                execute: visited.has(node.id),
                next: executionMap[node.id]?.next || []
            }
        }));
    };

    const onConnect = useCallback((params) => {
        if (!params.source || !params.target) return;

        setEdges((eds) =>
            addEdge(
                {
                    ...params,
                    animated: false,
                    style: {
                        stroke: "var(--originalGray)",
                        strokeWidth: 4,
                    },
                },
                eds
            )
        );
    }, [setEdges]);

    const onEdgesDelete = useCallback((deletedEdges) => {

        setEdges((eds) =>
            eds.filter(
                (e) => !deletedEdges.some((de) => de.id === e.id)
            )
        );
    }, [setEdges]);

    const getNextPosition = (nodes) => {
        if (nodes.length === 0) {
            return FIRST_POSITION;
        }

        const lastNode = nodes.reduce((max, n) =>
            n.position.x > max.position.x ? n : max
        );

        return {
            x: lastNode.position.x + SPACING_X,
            y: FIRST_POSITION.y
        };
    };

    const applyRepulsion = (newNode, nodes) => {
        let { x, y } = newNode.position;

        nodes.forEach((n) => {
            const dx = x - n.position.x;
            const dy = y - n.position.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            const minDistance = 120;

            if (distance < minDistance) {
                const angle = Math.atan2(dy, dx);
                x += Math.cos(angle) * (minDistance - distance);
                y += Math.sin(angle) * (minDistance - distance);
            }
        });

        if (nodes.length == 0) {
            return { x: 450, y: 250 };
        } else {
            return { x, y }
        }
    };

    const addNode = (nodeType, item, params) => {
        const registry = NODE_REGISTRY[nodeType];
        if (!registry) return null;

        let newNode = null;

        setNodes((prev = []) => {
            if (!Array.isArray(prev)) prev = [];

            let updated = [...prev];

            const isFirstRealNode = updated.some(n => n.type === "default");

            if (isFirstRealNode && nodeType === "manual" || nodeType === "schedule") {
                updated = updated.filter((n) => n.type !== "default");
            }

            if (nodeType === "manual" || nodeType === "schedule") {
                updated = updated.filter((n) => n.data?.nodeType !== "manual" || n.data?.nodeType !== "schedule");
            }

            const position = applyRepulsion(
                { position: getNextPosition(updated) },
                updated
            );

            newNode = {
                id: `${nodeType}_${Date.now()}`,
                type: nodeType,
                position,
                data: {
                    nodeType,
                    params,
                    config: registry.build(item),
                    meta: {
                        label: item?.label || "Untitled",
                        description: item?.description || "",
                    },
                },
            };

            return [...updated, newNode];
        });

        return newNode;
    };

    const preventOverlap = (nodes) => {
        return nodes.map((node) => {
            let { x, y } = node.position;

            nodes.forEach((other) => {
                if (node.id === other.id) return;

                const dx = x - other.position.x;
                const dy = y - other.position.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < MIN_DISTANCE && distance > 0) {
                    const angle = Math.atan2(dy, dx);
                    const push = (MIN_DISTANCE - distance) * 0.5;

                    x += Math.cos(angle) * push;
                    y += Math.sin(angle) * push;
                }
            });

            return {
                ...node,
                position: { x, y },
            };
        });
    };

    const onNodesChange = useCallback((changes) => {
        setOrderedNodes(buildExecutionGraph(nodes, edges))

        setNodes((nds) => {
            let updated = applyNodeChanges(changes, nds);

            const isDragging = changes.some(c => c.type === "position" && c.dragging);

            if (isDragging) {
                updated = preventOverlap(updated);
            }

            return updated;
        });
    }, [setNodes]);

    const resolveParams = (params, executionHistory) => {
        if (!params) return params;

        if (Array.isArray(params)) {
            return params.map(p => resolveParams(p, executionHistory));
        }

        if (typeof params === "object") {
            // dynamic value
            if (params.type === "dynamic") {
                const sourceOutput = executionHistory[params.sourceNodeId]?.output;

                if (!sourceOutput) return null;

                if (!params.path) return sourceOutput;

                return sourceOutput[params.path];
            }

            // normal object → recurse
            const resolved = {};
            for (const key in params) {
                resolved[key] = resolveParams(params[key], executionHistory);
            }

            return resolved;
        }

        return params;
    };

    const executeNode = async (node = {}) => {
        const resolvedNode = {
            ...node,
            data: {
                ...node.data,
                params: resolveParams(node.data?.params, executionHistory)
            }
        };
        try {
            const response = await fetch(`${GEN_WORKFLOW_LINK}/execute_node`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    wf_id,
                    node: resolvedNode
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to execute node");
            }

            const output = await response.json();
            console.log(output)

            setExecutionOutput(output);

            setExecutionHistory(prev => ({
                ...prev,
                [node.id]: output
            }));
            console.log("EXEC OUTPUT RAW:", output);

            return output;

        } catch (err) {
            console.error("Node execution error:", err);

            const errorOutput = {
                nodeId: node.id,
                nodeType: node.type,
                error: true,
                message: err.message,
                timestamp: Date.now()
            };

            setExecutionOutput(errorOutput);

            setExecutionHistory(prev => ({
                ...prev,
                [node.id]: errorOutput
            }));

            return errorOutput;
        }
    };

    const executeWorkflow = async (nodes, edges) => {
        const graph = buildGraph(nodes, edges);
        const visited = new Set();

        const startNodes = nodes.filter(n => n.type === "manual" || n.type === "schedule");

        let lastOutput = null;

        const run = async (nodeId, input = {}) => {
            const node = nodes.find(n => n.id === nodeId);
            if (!node || visited.has(nodeId)) return;

            visited.add(nodeId);

            const output = await executeNode(node, input);

            lastOutput = output;
            setExecutionOutput(output);

            const children = graph.get(nodeId) || [];

            for (const childId of children) {
                await run(childId, output);
            }
        };

        for (const start of startNodes) {
            await run(start.id, {});
        }

        return lastOutput;
    };

    const handleSave = async () => {
        const endOrder = buildExecutionGraph(nodes, edges)

        const result = await executeWorkflow(endOrder, edges);

        console.log("EXECUTION RESULT:");
        console.log(endOrder);
    };

    const OnSelectNode = (type, item, params, nodeId = null) => {

        if (nodeId) {
            setNodes((nds) =>
                nds.map((node) =>
                    node.id === nodeId
                        ? {
                            ...node,
                            type: type,
                            data: {
                                ...node.data,
                                nodeType: type,
                                params,
                                config: NODE_REGISTRY[type].build(item),
                                meta: {
                                    label: item?.label || "Untitled",
                                    description: item?.description || "",
                                },
                            },
                        }
                        : node
                )
            );

            return;
        }

        const newNode = addNode(type, item, params);

        if (pendingConnectionSource && newNode) {
            setEdges((eds) =>
                addEdge(
                    {
                        source: pendingConnectionSource,
                        target: newNode.id,
                        animated: false,
                        style: {
                            stroke: "var(--originalGray)",
                            strokeWidth: 4,
                        },
                    },
                    eds
                )
            );

            setPendingConnectionSource(null);
        }
    }

    useEffect(() => {
        setNodes((prev) => {
            const hasTrigger = prev.some(
                (n) => n.type === "manual" || n.type === "schedule"
            );

            const hasDefault = prev.some((n) => n.type === "default");

            if (!hasTrigger && !hasDefault) {
                return [
                    ...prev,
                    {
                        id: "default_start",
                        type: "default",
                        position: { x: 450, y: 250 },
                        data: { label: "Start" },
                    },
                ];
            }

            if (hasTrigger && hasDefault) {
                return prev.filter((n) => n.type !== "default");
            }

            return prev;
        });
    }, [nodes]);

    useEffect(() => {
        const hasTrigger = nodes.some(n => n.type === "schedule" || n.type === "manual");

        if (!hasTrigger) {
            setTypeNodeTrigger(true);
        } else {
            setTypeNodeTrigger(false)
        }
    }, [nodes]);

    useEffect(() => {
        setOrderedNodes(enrichNodesWithExecution(buildExecutionGraph(nodes, edges)));
    }, [nodes, edges]);


    return (
        <div className="workflow_cont">
            <div className="workflow_flow_cont">
                <div className="new_automation_btn_cont">
                    {loadingWfData ? (
                        <button className="main_mcp_cont_chat_p fade-in main_mcp_cont_chat_btn_loading"></button>
                    ) : (
                        wfData.mcp_name ? (
                            <p className="main_mcp_cont_chat_title fade-in" >Name</p>
                        ) : (
                            <p className="main_mcp_cont_chat_title">Workflow builder</p>
                        )
                    )}
                    <div className="main_mcp_cont_chat_btn_cont">
                        <button className="main_mcp_cont_chat_btn fade-in" onClick={handleSave}>Save</button>
                        <button className="main_mcp_cont_chat_btn fade-in" onClick={toggleCodeView}>
                            {openAiChat ? <TbLayoutSidebarLeftExpand /> : <TbLayoutSidebarRightExpandFilled />}
                        </button>
                    </div>
                </div>

                {loadingWfData ? (
                    <WorkflowLoader />
                ) : (
                    <FlowFrame
                        mcpData={mcpData}
                        fetchMcpData={fetchMcpData}
                        loadingMcpData={loadingMcpData}

                        dropdownRef={dropdownRef}
                        openOverDropdown={openOverDropdown}
                        setOpenOverDropdown={setOpenOverDropdown}
                        onSelectNode={OnSelectNode}

                        toggleOverDropdown={toggleOverDropdown}
                        nodes={enrichNodesWithExecution(nodes)}
                        setSelectedNodeId={setSelectedNodeId}
                        setSelectedNode={setSelectedNode}
                        selectedNode={selectedNode}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        typeNodeTrigger={typeNodeTrigger}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onEdgesDelete={onEdgesDelete}
                        onConnect={onConnect}
                        orderedNodes={orderedNodes}

                        openInputWorkflow={openInputWorkflow}
                        setOpenInputWorkflow={setOpenInputWorkflow}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        executeNode={executeNode}
                        executionHistory={executionHistory}
                    />
                )}
            </div>
        </div>
    );
};

export default CreateWorkflow;